import { useEffect, useRef } from 'react';

import { degreesToRadians } from '../angleUtils';
import { MARGIN_TOP } from '../consts';
import styles from '../DirectionalDrillingMonitor.module.css';

interface AimProps {
  theme: 'dark' | 'light';
  width: number;
  height: number;
  is360: boolean;
  pixelRatio: number;
}

export function Aim({ width, height, is360, pixelRatio, theme }: AimProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const s = Math.min(width, height);
  const r = s / 2 - MARGIN_TOP;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }

    canvas.width = width * pixelRatio;
    canvas.height = height * pixelRatio;
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;

    const ctx = canvas.getContext('2d');
    if (ctx && r > 0) {
      ctx.scale(pixelRatio, pixelRatio);

      const cx = width / 2;
      const cy = height / 2;
      const rounds = 5;
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      const colors = getComputedStyle(canvas);
      ctx.strokeStyle = colors.getPropertyValue('--go-widgets-chart-grid-color');

      ctx.fillStyle = colors.getPropertyValue('--go-widgets-chart-text-color');

      for (let i = 1; i <= rounds; i++) {
        const rmin = r / rounds;
        ctx.beginPath();
        ctx.arc(cx, cy, rmin * i, 0, 2 * Math.PI);
        ctx.stroke();
        ctx.closePath();
      }

      for (let i = 0; i < 360; i += 10) {
        if (i % 30 === 0) {
          continue;
        }

        const rad = degreesToRadians(i);
        const x = cx + Math.sin(rad) * (r - 1);
        const y = cy - Math.cos(rad) * (r - 1);

        const re = r - 5;
        const x1 = cx + Math.sin(rad) * re;
        const y2 = cy - Math.cos(rad) * re;
        ctx.beginPath();
        ctx.moveTo(x, y);
        ctx.lineTo(x1, y2);
        ctx.stroke();
        ctx.closePath();
      }

      for (let i = 0; i < 360; i += 30) {
        const rad = degreesToRadians(i);
        const x = cx + Math.sin(rad) * (r - 1);
        const y = cy - Math.cos(rad) * (r - 1);

        const rt = r + 13;
        const xt = cx + Math.sin(rad) * rt - String(i).length * 3 * (i > 180 ? 2 : 1);
        const yt = cy - Math.cos(rad) * rt + 4;

        ctx.beginPath();
        ctx.moveTo(cx, cy);
        ctx.lineTo(x, y);
        ctx.font = '400 13px/16px Inter';
        const text = is360 ? i : i - 360;
        ctx.fillText(String(i <= 180 ? i : text), xt, yt);
        ctx.stroke();
        ctx.closePath();
      }
    }
  }, [pixelRatio, height, is360, r, theme, width]);

  return (
    <canvas
      ref={canvasRef}
      width={width * pixelRatio}
      height={height * pixelRatio}
      className={styles.canvas}
      aria-hidden="true"
      style={{ width, height }}
    />
  );
}
